import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BetonOrderCalcResult, BetonOrderDeliveryAddress, BetonOrderFormInitialData } from '@beton/const';
import { BetonOrderService } from '@beton/feature';
import { CityService } from '@city/feature';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SelectedPrice } from '@product/const';
import { StateUtils } from '@spartacus/core';
import { AddressSuggestionsService } from '@yandex-maps/feature';
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs';
import { BETON_PRICE_DATA } from './beton-price-data';
import { ValidateDeliveryAddress } from './delivery-address.validator';

interface AddressSuggestion {
  label: string;
  value: BetonOrderDeliveryAddress;
}

@Component({
  selector: 'sd-beton-order-form',
  templateUrl: './beton-order-form.component.html',
  styleUrls: ['./beton-order-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddressSuggestionsService],
  standalone: false,
})
export class BetonOrderFormComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  orderForm: FormGroup;

  addressSuggestionsQuery$ = new Subject<string>();
  addressSuggestionsItems$: Observable<AddressSuggestion[]>;
  addressSuggestionsLoading$: Observable<boolean>;

  initialDataState$: Observable<StateUtils.LoaderState<BetonOrderFormInitialData>>;
  calculateOrderState$: Observable<StateUtils.LoaderState<BetonOrderCalcResult>>;
  calculateOrderErrors$: Observable<string[]>;
  confirmOrderState$: Observable<StateUtils.LoaderState<unknown>>;
  confirmOrderErrors$: Observable<string[]>;

  @ViewChild('deliveryAddressRef', { read: NgSelectComponent }) deliveryAddressSelect: NgSelectComponent;

  constructor(
    private fb: FormBuilder,
    private betonOrderService: BetonOrderService,
    private addressSuggestionsService: AddressSuggestionsService,
    private cityService: CityService,
    @Inject(BETON_PRICE_DATA) public betonPriceData: SelectedPrice,
  ) {}

  ngOnInit(): void {
    this.orderForm = this.fb.group({
      betonTypeCode: this.fb.control(null, [Validators.required]),
      volumeCbm: this.fb.control(0.5, [Validators.required]),
      deliveryAddress: this.fb.control(null, { validators: [Validators.required, ValidateDeliveryAddress] }),
      needBetonPump: this.fb.control(false, [Validators.required]),
      phone: this.fb.control(null, { validators: [Validators.required, Validators.minLength(10)] }),
      agreement: this.fb.control(true, { validators: [Validators.requiredTrue] }),
    });

    this.initialDataState$ = this.betonOrderService.getInitialDataState();
    this.calculateOrderState$ = this.betonOrderService.getCalculateOrderState();
    this.confirmOrderState$ = this.betonOrderService.getConfirmOrderState();

    this.calculateOrderErrors$ = this.betonOrderService.getCalculateOrderError().pipe(
      map((error) => {
        if (error?.details) {
          return error.details.map((i) => i.message) || [];
        }

        return undefined;
      }),
    );

    this.confirmOrderErrors$ = this.betonOrderService.getConfirmOrderError().pipe(
      map((error) => {
        if (error?.details) {
          return error.details.map((i) => i.message) || [];
        }

        return undefined;
      }),
    );

    this.addressSuggestionsLoading$ = this.addressSuggestionsService.loading$;
    this.addressSuggestionsItems$ = this.addressSuggestionsService.suggestions$.pipe(
      map((itemsArr) => {
        return itemsArr.map((item: any) => ({
          label: item.value,
          value: {
            formattedAddress: item.value,
            point: {
              latitude: item.data.geo_lat,
              longitude: item.data.geo_lon,
            },
            streetName: item.data.street,
            streetNumber: item.data.house,
            town: item.city,
          },
        }));
      }),
    );

    // заполнение формы дефолтными значениями с бекенда
    this.subscription.add(
      this.betonOrderService.getInitialDataState().subscribe((initialDataState) => {
        if (initialDataState.success && initialDataState.value) {
          this.orderForm.patchValue({
            betonTypeCode: initialDataState.value.types[0].code,
          });
        }
      }),
    );

    // запрос подсказок при вводе адреса
    this.subscription.add(
      this.addressSuggestionsQuery$.pipe(debounceTime(700)).subscribe((query: string) => {
        this.addressSuggestionsService.loadSuggestions(query);
      }),
    );

    // пересчет заказа при изменении полей формы
    this.subscription.add(
      this.orderForm.valueChanges
        .pipe(
          debounceTime(700),
          filter((value) => {
            return (
              !value.deliveryAddress ||
              (value.deliveryAddress &&
                value.deliveryAddress.point.latitude &&
                value.deliveryAddress.point.longitude &&
                value.deliveryAddress.streetNumber)
            );
          }),
          // пересчитываем заказ только при изменении определенных полей формы
          distinctUntilChanged((prev, current) => {
            return (
              prev.betonTypeCode === current.betonTypeCode &&
              prev.volumeCbm === current.volumeCbm &&
              prev.deliveryAddress === current.deliveryAddress &&
              prev.needBetonPump === current.needBetonPump
            );
          }),
        )
        .subscribe((value) => {
          this.betonOrderService.calculateOrder(value);
        }),
    );

    // блокирование полей формы при оформлении заявки
    this.subscription.add(
      this.betonOrderService.getConfirmOrderState().subscribe((state) => {
        if (state.loading && !this.orderForm.disabled) {
          this.orderForm.disable();
        }

        if (!state.loading && !this.orderForm.enabled) {
          this.orderForm.enable();
        }
      }),
    );

    // блокирование полей формы при заполнении дефолтными значениями
    this.subscription.add(
      this.betonOrderService.getInitialDataState().subscribe((state) => {
        if (state.loading && !this.orderForm.disabled) {
          this.orderForm.disable();
        }

        if (!state.loading && !this.orderForm.enabled) {
          this.orderForm.enable();
        }
      }),
    );

    this.betonOrderService.loadInitialData();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.betonOrderService.resetInitialDataState();
    this.betonOrderService.resetCalcOrderState();
    this.betonOrderService.resetConfirmOrderState();
  }

  onDeliveryAddressOpen(): void {
    if (this.deliveryAddressSelect.searchTerm) {
      return;
    }

    this.cityService.activeBaseStore$.pipe(take(1)).subscribe((currentCity) => {
      this.deliveryAddressSelect.filter(`г ${currentCity.name} `);
    });
  }

  submitForm(): void {
    if (this.orderForm.invalid) {
      this.orderForm.markAllAsTouched();

      return;
    }

    this.calculateOrderState$.pipe(take(1)).subscribe((state) => {
      this.betonOrderService.confirmOrder({
        ...this.orderForm.value,
        phone: '7' + this.orderForm.value.phone,
        previousCalculationTotalPrice: state.value?.totalPrice,
      });
    });
  }
}
